import {useCallback, useEffect, useRef, useState} from "react";
import {getApi} from "../../../apis/backendApis";
import {aguardaEntregaLocal, ExecutionResult, isAtivo} from "./executionResults";

const INTERVALO_MS = 3000;
// Sem nenhuma mudança por um tempo (ex.: um caso longo), consulta com menos frequência
const INTERVALO_OCIOSO_MS = 15000;
const OCIOSO_APOS_MS = 120000;
const INTERVALO_ERRO_MS = 5000;

const assinatura = (results: Array<ExecutionResult>) =>
    results.map(r => r.id + ':' + r.status + ':' + r.amount_success + ':' + r.amount_erros + ':' + (r.hasReport ? 1 : 0) + ':' + (r.enviado ? 1 : 0)).join('|');

/**
 * Resultados do plano, atualizados sozinhos enquanto houver caso na fila ou executando.
 * Mantém a lista na tela durante as recargas (sem piscar) e pausa com a aba em segundo plano.
 */
const useExecutionPolling = (planoId: string | undefined) => {
    const [results, setResults] = useState<Array<ExecutionResult>>([]);
    const [carregando, setCarregando] = useState<boolean>(true);
    const [erro, setErro] = useState<string | null>(null);
    const [atualizadoEm, setAtualizadoEm] = useState<Date | null>(null);

    const timerRef = useRef<number | null>(null);
    const montadoRef = useRef<boolean>(true);
    const assinaturaRef = useRef<string>('');
    const mudouEmRef = useRef<number>(Date.now());
    const falhouRef = useRef<boolean>(false);
    const entregandoRef = useRef<boolean>(false);

    const limparTimer = () => {
        if (timerRef.current !== null) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const recarregar = useCallback(async (): Promise<Array<ExecutionResult>> => {
        if (!planoId) {
            return [];
        }
        try {
            const response = await getApi().get('/execute-task/result/' + planoId);
            const lista: Array<ExecutionResult> = Array.isArray(response.data) ? response.data : [];
            if (!montadoRef.current) {
                return lista;
            }
            const nova = assinatura(lista);
            if (nova !== assinaturaRef.current) {
                assinaturaRef.current = nova;
                mudouEmRef.current = Date.now();
                setResults(lista);
            }
            falhouRef.current = false;
            setErro(null);
            setAtualizadoEm(new Date());
            return lista;
        } catch (error) {
            falhouRef.current = true;
            if (montadoRef.current) {
                setErro('Não foi possível atualizar os resultados. Tentando de novo...');
            }
            return [];
        } finally {
            if (montadoRef.current) {
                setCarregando(false);
            }
        }
    }, [planoId]);

    const temAtivos = results.some(isAtivo);
    // Enquanto a tela entrega casos ao executor local, não pausa com a aba em segundo plano nem desacelera
    entregandoRef.current = results.some(aguardaEntregaLocal);
    const comErro = erro !== null;

    // Agenda a próxima consulta só depois que a anterior voltou: nunca empilha requisições
    useEffect(() => {
        limparTimer();
        if (!temAtivos && !comErro) {
            return;
        }
        const agendar = () => {
            if (document.hidden && !entregandoRef.current) {
                return;
            }
            const ocioso = !entregandoRef.current && Date.now() - mudouEmRef.current > OCIOSO_APOS_MS;
            const intervalo = falhouRef.current ? INTERVALO_ERRO_MS : (ocioso ? INTERVALO_OCIOSO_MS : INTERVALO_MS);
            timerRef.current = window.setTimeout(async () => {
                timerRef.current = null;
                await recarregar();
                if (montadoRef.current) {
                    agendar();
                }
            }, intervalo);
        };
        agendar();

        const aoVoltarParaAba = () => {
            if (!document.hidden && timerRef.current === null) {
                recarregar().then(() => montadoRef.current && agendar());
            }
        };
        document.addEventListener('visibilitychange', aoVoltarParaAba);
        return () => {
            document.removeEventListener('visibilitychange', aoVoltarParaAba);
            limparTimer();
        };
    }, [temAtivos, comErro, recarregar]);

    useEffect(() => {
        montadoRef.current = true;
        recarregar();
        return () => {
            montadoRef.current = false;
            limparTimer();
        };
    }, [recarregar]);

    /** Volta ao ritmo rápido (ex.: logo depois de disparar uma execução) */
    const acordar = useCallback(async () => {
        mudouEmRef.current = Date.now();
        return recarregar();
    }, [recarregar]);

    return {results, carregando, erro, atualizadoEm, temAtivos, recarregar: acordar};
}

export default useExecutionPolling;
